import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';
import { app } from '../firebase';
import { db } from '../lib/firebase';
import {
  FRESH_MS,
  classifySession,
  deviceAgeMs,
  groupSessionsByBoat,
  sessionFreshnessMs,
} from '../lib/sessionGrouping';
import { deriveSessionStats } from '../lib/sessionStats';

// Colour for a last-event age: green while transmitting, red once stale.
function freshColor(ms, staleColor = '#dc3545') {
  return ms != null && ms < FRESH_MS ? '#198754' : staleColor;
}

const REFRESH_INTERVAL_MS = 30_000;

function fmtTime(d) {
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-IE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function ago(ms) {
  if (ms == null || !Number.isFinite(ms)) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

const BOAT_STATUS_STYLE = {
  transmitting: { label: 'Transmitting', bg: '#198754', fg: '#fff' },
  mixed: { label: 'Mixed', bg: '#f59e0b', fg: '#fff' },
  stale: { label: 'Stale', bg: '#dc3545', fg: '#fff' },
  finished: { label: 'Finished', bg: '#e0e0e0', fg: '#444' },
};

const SESSION_STATUS_STYLE = {
  transmitting: { label: 'live', bg: '#198754', fg: '#fff' },
  orphaned: { label: 'orphan', bg: '#dc3545', fg: '#fff' },
  finished: { label: 'finished', bg: '#e0e0e0', fg: '#444' },
};

function StatCard({ value, label, accent }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        padding: '1.25rem 1rem',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
      }}
    >
      <span style={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1.1, color: accent || '#1a1a2e' }}>
        {value}
      </span>
      <span
        style={{
          fontSize: '0.75rem',
          color: '#666',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </span>
    </div>
  );
}

function Pill({ label, bg, fg }) {
  return (
    <span
      style={{
        fontSize: '0.68rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        padding: '0.12rem 0.45rem',
        borderRadius: 3,
        background: bg,
        color: fg,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

// One session within a device group (or a flattened boat). The `device` row,
// when present, supplies the per-device event count and last-event age; without
// it we fall back to the session-wide freshness.
function SessionLine({ session, device, status, multiClass, nowMs }) {
  const started = session.startedAt ? new Date(session.startedAt) : null;
  const finished = session.finishedAt ? new Date(session.finishedAt) : null;
  const durationMin = started && finished ? Math.round((finished - started) / 60_000) : null;
  const elapsedMin = started && !finished ? Math.round((nowMs - started.getTime()) / 60_000) : null;

  const eventCount = device ? device.eventCount : null;
  const lastEventMs = device ? deviceAgeMs(device, nowMs) : sessionFreshnessMs(session, nowMs);
  const style = SESSION_STATUS_STYLE[status] || SESSION_STATUS_STYLE.finished;

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.4rem 1.1rem',
        fontSize: '0.82rem',
        color: '#444',
        padding: '0.4rem 0',
        borderTop: '1px solid #f1f3f5',
      }}
    >
      <Pill label={style.label} bg={style.bg} fg={style.fg} />
      <span>
        <strong style={{ color: '#1a1a2e' }}>Course:</strong> {session.courseName || '—'}
      </span>
      {multiClass && (
        <span>
          <strong style={{ color: '#1a1a2e' }}>Class:</strong> {session.fleetClass}
        </span>
      )}
      <span>
        <strong style={{ color: '#1a1a2e' }}>Started:</strong> {fmtTime(started)}
      </span>
      {finished ? (
        <span>
          <strong style={{ color: '#1a1a2e' }}>Duration:</strong> {durationMin}m
        </span>
      ) : (
        elapsedMin != null && (
          <span>
            <strong style={{ color: '#1a1a2e' }}>Elapsed:</strong> {elapsedMin}m
          </span>
        )
      )}
      {eventCount != null && <span>{eventCount} events</span>}
      <span style={{ color: freshColor(lastEventMs) }}>
        last event {ago(lastEventMs)} ago
      </span>
      <span>
        <a href={`/race-live?s=${encodeURIComponent(session.sessionPath)}`}>Race Live</a>
        {' · '}
        <a href={`/admin/diagnostics?r=${encodeURIComponent(session.sessionPath)}`}>Diagnostics</a>
      </span>
    </div>
  );
}

function DeviceGroup({ group, multiClass, nowMs }) {
  return (
    <div style={{ marginTop: '0.6rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          fontSize: '0.78rem',
          color: '#666',
        }}
      >
        <code
          style={{
            background: '#f5f5f7',
            padding: '0.1rem 0.4rem',
            borderRadius: 3,
            fontSize: '0.74rem',
          }}
        >
          {(group.deviceId || '?').slice(0, 12)}
        </code>
        <span>
          {group.sessionCount} session{group.sessionCount === 1 ? '' : 's'}
        </span>
        <span style={{ color: freshColor(group.lastEventMs) }}>
          last event {ago(group.lastEventMs)} ago
        </span>
      </div>
      {group.entries.map(({ session, device }) => (
        <SessionLine
          key={session.sessionPath}
          session={session}
          device={device}
          status={classifySession(session, nowMs)}
          multiClass={multiClass}
          nowMs={nowMs}
        />
      ))}
    </div>
  );
}

function BoatCard({ boat, expanded, onToggle, nowMs }) {
  const statusStyle = BOAT_STATUS_STYLE[boat.status] || BOAT_STATUS_STYLE.finished;
  const accent = boat.status === 'finished' ? '#9ca3af' : statusStyle.bg;

  const counts = [];
  if (boat.activeCount) counts.push(`${boat.activeCount} live`);
  if (boat.orphanCount) counts.push(`${boat.orphanCount} orphan`);
  if (boat.finishedCount) counts.push(`${boat.finishedCount} finished`);

  // Flatten when the boat has 0 or 1 devices; otherwise show device sub-groups.
  const flatten = boat.deviceCount <= 1;
  const flatEntries = flatten
    ? [
        ...boat.deviceGroups.flatMap((g) => g.entries),
        ...boat.sessionsWithoutDevices.map((session) => ({ session, device: null })),
      ].sort((a, b) => new Date(b.session.startedAt) - new Date(a.session.startedAt))
    : [];

  return (
    <div
      style={{
        border: '1px solid #e0e0e0',
        borderLeft: `4px solid ${accent}`,
        borderRadius: 8,
        background: '#fff',
        marginBottom: '0.75rem',
      }}
    >
      <button
        type="button"
        onClick={() => onToggle(boat.sailNo)}
        aria-expanded={expanded}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.9rem 1.1rem',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '0.5rem 0.9rem',
        }}
      >
        <span style={{ color: '#888', fontSize: '0.85rem', width: '0.8rem' }}>
          {expanded ? '▾' : '▸'}
        </span>
        <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e' }}>
          {boat.sailNo}
        </span>
        <Pill label={statusStyle.label} bg={statusStyle.bg} fg={statusStyle.fg} />
        <span style={{ fontSize: '0.82rem', color: '#444' }}>
          {boat.classes.join(', ') || '—'}
        </span>
        <span style={{ fontSize: '0.82rem', color: '#888' }}>
          {boat.clubs.join(', ')}
        </span>
        <span style={{ flex: 1 }} />
        {counts.length > 0 && (
          <span style={{ fontSize: '0.8rem', color: '#555' }}>{counts.join(' · ')}</span>
        )}
        <span style={{ fontSize: '0.78rem', color: freshColor(boat.lastEventMs, '#999') }}>
          {boat.lastEventMs != null ? `last event ${ago(boat.lastEventMs)} ago` : 'no events'}
        </span>
      </button>

      {expanded && (
        <div style={{ padding: '0 1.1rem 0.9rem', borderTop: '1px solid #f1f3f5' }}>
          {flatten ? (
            flatEntries.length === 0 ? (
              <p className="empty-state" style={{ margin: '0.6rem 0' }}>
                No sessions
              </p>
            ) : (
              <div style={{ marginTop: '0.4rem' }}>
                {flatEntries.map(({ session, device }) => (
                  <SessionLine
                    key={session.sessionPath}
                    session={session}
                    device={device}
                    status={classifySession(session, nowMs)}
                    multiClass={boat.multiClass}
                    nowMs={nowMs}
                  />
                ))}
              </div>
            )
          ) : (
            <>
              {boat.deviceGroups.map((group) => (
                <DeviceGroup
                  key={group.deviceId}
                  group={group}
                  multiClass={boat.multiClass}
                  nowMs={nowMs}
                />
              ))}
              {boat.sessionsWithoutDevices.length > 0 && (
                <div style={{ marginTop: '0.6rem' }}>
                  <div style={{ fontSize: '0.78rem', color: '#999' }}>No device reported</div>
                  {boat.sessionsWithoutDevices.map((session) => (
                    <SessionLine
                      key={session.sessionPath}
                      session={session}
                      device={null}
                      status={classifySession(session, nowMs)}
                      multiClass={boat.multiClass}
                      nowMs={nowMs}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function LiveActivity() {
  const functions = useMemo(() => getFunctions(app), []);
  const getActiveSessions = useMemo(
    () => httpsCallable(functions, 'getActiveSessions'),
    [functions],
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [statusOverrides, setStatusOverrides] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());

  const refreshTimerRef = useRef(null);
  const unsubsRef = useRef([]);
  const seenBoatsRef = useRef(new Set());

  const fetchSessions = useCallback(async () => {
    try {
      const result = await getActiveSessions({ hours: 24 });
      setData(result.data);
      setRefreshedAt(new Date());
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  }, [getActiveSessions]);

  useEffect(() => {
    fetchSessions();
    refreshTimerRef.current = setInterval(fetchSessions, REFRESH_INTERVAL_MS);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [fetchSessions]);

  // Attach onSnapshot listeners to _meta docs for active sessions so
  // status transitions (active → finished) reflect immediately without
  // waiting for the next 30s refresh tick.
  useEffect(() => {
    unsubsRef.current.forEach((u) => u());
    unsubsRef.current = [];
    setStatusOverrides({});

    if (!data?.active) return undefined;

    data.active.forEach((session) => {
      const metaRef = doc(db, session.sessionPath, '_meta');
      const unsub = onSnapshot(
        metaRef,
        (snap) => {
          if (!snap.exists()) return;
          const m = snap.data();
          if (m.status && m.status !== 'active') {
            setStatusOverrides((prev) => ({ ...prev, [session.sessionPath]: m.status }));
          }
        },
        () => {
          // Silently ignore listener errors (rules / offline)
        },
      );
      unsubsRef.current.push(unsub);
    });

    return () => {
      unsubsRef.current.forEach((u) => u());
      unsubsRef.current = [];
    };
  }, [data]);

  // Reference time captured per data refresh so ages don't churn between renders.
  const nowMs = useMemo(() => Date.now(), [data, statusOverrides]);

  const boats = useMemo(() => {
    if (!data) return [];
    const merged = [...(data.active || []), ...(data.finished || [])].map((s) => ({
      ...s,
      status: statusOverrides[s.sessionPath] || s.status,
    }));
    return groupSessionsByBoat(merged, nowMs);
  }, [data, statusOverrides, nowMs]);

  // Auto-expand boats that still have an active or orphaned session the first
  // time we see them; leave finished boats collapsed. User toggles win after.
  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const b of boats) {
        if (seenBoatsRef.current.has(b.sailNo)) continue;
        seenBoatsRef.current.add(b.sailNo);
        if (b.status !== 'finished') {
          next.add(b.sailNo);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [boats]);

  const toggleBoat = useCallback((sailNo) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(sailNo)) next.delete(sailNo);
      else next.add(sailNo);
      return next;
    });
  }, []);

  // Counters dedupe at the right unit: boats = distinct sail_no, devices =
  // distinct device UUID, sessions = session rows (ra-udhq.2). nowMs is
  // recomputed each data refresh, so freshness windows track wall-clock.
  // Re-bucket sessions whose live _meta override flipped them out of `active`
  // (e.g. → finished) so the tiles agree with the boat list's real-time status
  // instead of lagging until the next 30s refresh.
  const stats = useMemo(() => {
    if (!data) return deriveSessionStats(null, nowMs);
    const active = [];
    const finished = [...(data.finished || [])];
    for (const s of data.active || []) {
      const status = statusOverrides[s.sessionPath] || s.status;
      if (status && status !== 'active') finished.push(s);
      else active.push(s);
    }
    return deriveSessionStats({ active, finished }, nowMs);
  }, [data, statusOverrides, nowMs]);

  return (
    <>
      <PortalHeader />
      <div className="page">
        <PageHeader backTo="/admin" title="Live Activity" />

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.5rem',
            marginBottom: '1rem',
            fontSize: '0.85rem',
            color: '#666',
          }}
        >
          <span>
            {refreshedAt
              ? `Updated ${fmtTime(refreshedAt)} · auto-refresh 30s`
              : loading
              ? 'Loading…'
              : ''}
          </span>
          <button
            type="button"
            onClick={fetchSessions}
            disabled={loading}
            style={{
              padding: '4px 12px',
              fontSize: '0.85rem',
              border: '1px solid #ddd',
              background: '#fff',
              borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            Refresh now
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {loading && !data && <p>Loading sessions…</p>}

        {data && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem',
              }}
            >
              <StatCard
                value={stats.boatsOnWater}
                label="Boats on water"
                accent={stats.boatsOnWater > 0 ? '#198754' : '#1a1a2e'}
              />
              <StatCard
                value={stats.transmittingNow}
                label="Transmitting now"
                accent={stats.transmittingNow > 0 ? '#198754' : '#1a1a2e'}
              />
              <StatCard value={stats.activeSessions} label="Active sessions" />
              <StatCard value={stats.devices} label="Devices" />
              <StatCard
                value={stats.needsCleanup}
                label="Needs cleanup"
                accent={stats.needsCleanup > 0 ? '#dc3545' : '#1a1a2e'}
              />
              <StatCard value={stats.finished24h} label="Finished (24h)" />
            </div>

            <section>
              <h2
                style={{
                  fontSize: '1.1rem',
                  marginBottom: '0.75rem',
                  paddingBottom: '0.4rem',
                  borderBottom: '2px solid #e0e0e0',
                }}
              >
                Boats
              </h2>
              {boats.length === 0 ? (
                <p className="empty-state">No sessions in the last 24h</p>
              ) : (
                boats.map((boat) => (
                  <BoatCard
                    key={boat.sailNo}
                    boat={boat}
                    expanded={expanded.has(boat.sailNo)}
                    onToggle={toggleBoat}
                    nowMs={nowMs}
                  />
                ))
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
}
